import { actuacionTieneAcuerdo, calendarioItemTitulo } from './calendarioFiltros';

const WEEKDAYS_ES = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'];

export function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Construye la rejilla de 6 semanas del mes visible (misma lógica que Calendar.jsx).
 */
export function buildExportMonthGrid({
  currentMonth,
  calendarData = { fechasOcupadas: [], fechasDisponibles: [] },
  actividadesPorFecha = new Map(),
  showActividades = true,
}) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const today = new Date();
  const todayStr = toLocalDateString(today);
  const ocupadas = calendarData.fechasOcupadas || [];

  const firstDayOfMonth = new Date(year, month, 1);
  const startDate = new Date(firstDayOfMonth);
  const dayOfWeek = startDate.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(firstDayOfMonth.getDate() - daysToMonday);

  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    const dateString = toLocalDateString(date);
    const isCurrentMonth = date.getMonth() === month;
    const ocupada = ocupadas.find((f) => f.date === dateString);
    const acts = actividadesPorFecha.get(dateString) || { actuaciones: [], ensayos: [] };

    days.push({
      date: dateString,
      day: date.getDate(),
      isCurrentMonth,
      isToday: dateString === todayStr,
      status: ocupada ? 'ocupado' : 'disponible',
      ocupadaLabel: ocupada?.evento ? String(ocupada.evento).trim() : '',
      actuaciones: showActividades ? [...acts.actuaciones] : [],
      ensayos: showActividades ? [...acts.ensayos] : [],
    });
  }

  const monthTitle = firstDayOfMonth.toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });

  return {
    monthTitle: monthTitle.charAt(0).toUpperCase() + monthTitle.slice(1),
    days,
    weekdays: WEEKDAYS_ES,
  };
}

function truncateText(ctx, text, maxWidth) {
  const t = String(text || '').trim();
  if (!t) return '';
  if (ctx.measureText(t).width <= maxWidth) return t;
  let out = t;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function cellStyle(day) {
  if (!day.isCurrentMonth) {
    return { bg: '#F7FAFC', border: '#EDF2F7', text: '#CBD5E0', muted: true };
  }
  if (day.isToday) {
    return { bg: '#EBF8FF', border: '#63B3ED', text: '#2B6CB0', muted: false };
  }
  if (day.status === 'ocupado') {
    return { bg: '#FED7D7', border: '#FC8181', text: '#C53030', muted: false };
  }
  return { bg: '#C6F6D5', border: '#68D391', text: '#276749', muted: false };
}

/**
 * Dibuja la rejilla mensual en un canvas 2D.
 * @returns {number} altura usada
 */
export function drawMonthCalendarGrid(ctx, x, y, width, grid) {
  const { monthTitle, days, weekdays } = grid;
  const cols = 7;
  const rows = 6;
  const titleH = 44;
  const weekdayH = 28;
  const gap = 6;
  const gridTop = y + titleH;
  const cellW = (width - gap * (cols - 1)) / cols;
  const cellH = 88;

  ctx.fillStyle = '#4C1D95';
  ctx.font = '800 22px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(monthTitle, x, y + 8);

  for (let c = 0; c < cols; c += 1) {
    const cx = x + c * (cellW + gap);
    ctx.fillStyle = '#718096';
    ctx.font = '700 11px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(weekdays[c], cx + cellW / 2, gridTop + 6);
  }

  const bodyTop = gridTop + weekdayH;

  for (let i = 0; i < days.length; i += 1) {
    const day = days[i];
    const row = Math.floor(i / cols);
    const col = i % cols;
    const cx = x + col * (cellW + gap);
    const cy = bodyTop + row * (cellH + gap);
    const style = cellStyle(day);

    roundRect(ctx, cx, cy, cellW, cellH, 8);
    ctx.fillStyle = style.bg;
    ctx.fill();
    ctx.strokeStyle = style.border;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    if (style.muted) {
      ctx.globalAlpha = 0.45;
    }

    ctx.fillStyle = style.text;
    ctx.font = '800 16px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(String(day.day), cx + 8, cy + 6);

    let textY = cy + 28;
    const innerW = cellW - 12;

    if (day.isCurrentMonth && day.status === 'ocupado' && day.ocupadaLabel) {
      ctx.fillStyle = '#9B2C2C';
      ctx.font = '600 9px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
      ctx.fillText(truncateText(ctx, day.ocupadaLabel, innerW), cx + 6, textY);
      textY += 12;
    }

    const chips = [];
    for (const a of day.actuaciones.slice(0, 2)) {
      chips.push({
        label: truncateText(ctx, calendarioItemTitulo(a, 'actuacion'), innerW),
        color: actuacionTieneAcuerdo(a) ? '#3182CE' : '#DD6B20',
        bg: actuacionTieneAcuerdo(a) ? '#BEE3F8' : '#FEEBC8',
      });
    }
    for (const e of day.ensayos.slice(0, Math.max(0, 2 - day.actuaciones.length))) {
      chips.push({
        label: truncateText(ctx, calendarioItemTitulo(e, 'ensayo'), innerW),
        color: '#D53F8C',
        bg: '#FED7E2',
      });
    }

    const extra = day.actuaciones.length + day.ensayos.length - chips.length;
    for (const chip of chips.slice(0, 2)) {
      const chipH = 16;
      roundRect(ctx, cx + 5, textY, innerW, chipH, 4);
      ctx.fillStyle = chip.bg;
      ctx.fill();
      ctx.fillStyle = chip.color;
      ctx.font = '700 8px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
      ctx.fillText(chip.label, cx + 8, textY + 4);
      textY += chipH + 3;
      if (textY > cy + cellH - 14) break;
    }

    if (extra > 0 && textY <= cy + cellH - 12) {
      ctx.fillStyle = '#718096';
      ctx.font = '600 8px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
      ctx.fillText(`+${extra} más`, cx + 6, cy + cellH - 10);
    }

    ctx.globalAlpha = 1;
  }

  const totalH = titleH + weekdayH + rows * cellH + (rows - 1) * gap + 8;
  return totalH;
}

export function estimateMonthGridHeight() {
  return 44 + 28 + 6 * 88 + 5 * 6 + 8;
}
